// Renders the "where can I pay with Taler" map and the merchant list beside it.
//
// Data comes from /tsv/merchants.tsv, a tab-separated file with a header row.
// Any column is read into the merchant object; `latitude` and `longitude` are
// parsed as numbers and a marker is only placed when both are present, so a
// merchant with no coordinates still shows up in the table.
//
// Leaflet is loaded separately as a plain global script, hence the declare.
declare const L: any;

const TSV_URL = '/tsv/merchants.tsv';

interface Merchant {
  name?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  [key: string]: string | number | undefined;
}

/** Parse the TSV into merchant records, tolerating bad coordinate cells. */
function parseMerchants(text: string): Merchant[] {
  const rows = text.split(/\r?\n/);
  const headers = rows[0].split(/\t/).map((h) => h.trim().toLowerCase());

  return rows
    .slice(1)
    .filter((row) => row !== '')
    .map((row) => {
      const values = row.split(/\t/).map((value) => value.trim());
      return headers.reduce((merchant: Merchant, header, index) => {
        const raw = values[index] || '';
        if (raw && /(lat|long)itude/.test(header)) {
          const num = parseFloat(raw);
          if (isNaN(num)) {
            console.warn(`could not parse ${header} "${raw}" for [${values}]`);
          } else {
            merchant[header] = num;
          }
        } else {
          merchant[header] = raw;
        }
        return merchant;
      }, {});
    });
}

function hasLocation(merchant: Merchant): merchant is Merchant & {
  latitude: number;
  longitude: number;
} {
  return (
    typeof merchant.latitude === 'number' &&
    Number.isFinite(merchant.latitude) &&
    typeof merchant.longitude === 'number' &&
    Number.isFinite(merchant.longitude)
  );
}

function addTableRow(
  table: HTMLTableSectionElement,
  merchant: Merchant,
  showOnMap?: () => void,
): void {
  const row = table.insertRow();
  const name = document.createElement('b');
  name.textContent = merchant.name ?? '';
  row.insertCell().appendChild(name);
  const description = document.createElement('div');
  // The backend-managed TSV intentionally contains simple formatting and
  // merchant links, matching the behavior of the previous inline map script.
  description.innerHTML = merchant.description ?? '';
  row.insertCell().appendChild(description);

  if (showOnMap) {
    const actionCell = row.insertCell();
    actionCell.className = 'merchant-map-action';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'merchant-map-button';
    button.textContent = table.dataset.showOnMapLabel ?? 'Show on map';
    button.addEventListener('click', showOnMap);
    actionCell.appendChild(button);
  }
}

function setupPartnerTabs(): void {
  const panels = document.querySelector<HTMLElement>('[data-partner-panels]');
  const tabs = Array.from(
    document.querySelectorAll<HTMLButtonElement>('[data-partner-tab]'),
  );
  const indicator = document.querySelector<HTMLElement>(
    '[data-partner-indicator]',
  );
  if (!panels || tabs.length !== 2 || !indicator) return;

  const panelItems = Array.from(
    panels.querySelectorAll<HTMLElement>('[role="tabpanel"]'),
  );
  if (panelItems.length !== 2) return;

  let activeIndex = 0;
  const resizeToActivePanel = (): void => {
    panels.style.height = `${panelItems[activeIndex].scrollHeight}px`;
  };

  const selectTab = (index: number, scrollPanel: boolean): void => {
    activeIndex = index;
    tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    indicator.style.transform = `translateX(${index * 100}%)`;
    resizeToActivePanel();

    if (scrollPanel) {
      panels.scrollTo({
        left: panels.clientWidth * index,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
    }
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => selectTab(index, true));
  });

  let scrollFrame = 0;
  panels.addEventListener('scroll', () => {
    window.cancelAnimationFrame(scrollFrame);
    scrollFrame = window.requestAnimationFrame(() => {
      const index = Math.round(panels.scrollLeft / panels.clientWidth);
      selectTab(Math.max(0, Math.min(index, tabs.length - 1)), false);
    });
  });

  const panelObserver = new ResizeObserver(resizeToActivePanel);
  panelItems.forEach((panel) => panelObserver.observe(panel));
  window.addEventListener('resize', resizeToActivePanel);
  selectTab(0, false);
}

async function main(): Promise<void> {
  const container = document.getElementById('map');
  const physicalTable = document.getElementById(
    'physicalMerchantTable',
  ) as HTMLTableSectionElement | null;
  const digitalTable = document.getElementById(
    'digitalMerchantTable',
  ) as HTMLTableSectionElement | null;
  if (!container || !physicalTable || !digitalTable) return;

  setupPartnerTabs();

  const merchantIcon = L.divIcon({
    className: 'merchant-marker',
    html: '<svg viewBox="0 0 32 42" aria-hidden="true"><path fill="currentColor" d="M16 0C7.2 0 0 7.2 0 16c0 11.7 16 26 16 26s16-14.3 16-26C32 7.2 24.8 0 16 0Z"/><circle cx="16" cy="16" r="6"/></svg>',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -38],
  });
  const map = L.map('map');
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution:
      '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(map);

  const response = await fetch(TSV_URL, { cache: 'reload' });
  if (!response.ok) {
    throw new Error(`error retrieving merchant data, status ${response.status}`);
  }

  const markers = L.featureGroup().addTo(map);
  let selectedMarker: any = null;

  const selectMarker = (marker: any | null): void => {
    selectedMarker?.getElement()?.classList.remove('merchant-marker--selected');
    selectedMarker = marker;
    selectedMarker?.getElement()?.classList.add('merchant-marker--selected');
  };

  const showMarker = (marker: any): void => {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    container.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    map.flyTo(marker.getLatLng(), 17, {
      animate: !reducedMotion,
      duration: reducedMotion ? 0 : 0.75,
    });
    marker.openPopup();
  };

  for (const merchant of parseMerchants(await response.text())) {
    if (hasLocation(merchant)) {
      const marker = L.marker([merchant.latitude, merchant.longitude], {
        title: merchant.name,
        icon: merchantIcon,
      })
        .addTo(markers)
        .bindPopup(`<b>${merchant.name}</b><br>${merchant.description}`);
      marker.on('click', () => selectMarker(marker));
      marker.on('popupopen', () => selectMarker(marker));
      marker.on('popupclose', () => {
        if (selectedMarker === marker) selectMarker(null);
      });
      addTableRow(physicalTable, merchant, () => showMarker(marker));
    } else {
      addTableRow(digitalTable, merchant);
    }
  }

  const bounds = markers.getBounds();
  if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30] });
}

window.addEventListener('load', () => {
  main().catch((e) => console.error('merchant map failed:', e));
});
